import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {ModalDialogDismissOption} from '@shared/confirmation-dialog/modal-dialog-dismiss-option';

import {OrganizationService} from '@services/administration/organization/organization.service';
import {TranslateService} from '@ngx-translate/core';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {Organization} from '@models/organization/organization';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {BIGQUERY_VALID_NAME_REGEX, URL_REGEX} from '@constants/reg-exp';
import {OrganizationTableauConfiguration} from '@models/organization/organization-tableau-configuration';


@Component({
    selector: 'scitis-admin-organization-edit',
    templateUrl: './organization-edit.component.html',
    styleUrls: ['./organization-edit.component.scss'],
    standalone: false
})
export class OrganizationEditComponent implements OnInit, OnDestroy {


  @Input()
  isSuperAdminRole: boolean = false;

  @Input()
  rootOrganizationId: number = 0;

  @Input()
  organization: Organization = new Organization();

  organizationForm: UntypedFormGroup = new UntypedFormGroup({});

  isLoadingOrganizationParents: boolean = false;
  isNewOrganization: boolean = false;
  backupOrganizationName: string = '';

  possibleParents: Organization[] = new Array<Organization>();
  submitted: boolean = false;

  private destroy: Subject<boolean> = new Subject<boolean>();
  private tableauConfigurationFormIsInvalid: boolean = false;

  constructor(private activeModal: NgbActiveModal,
              private translate: TranslateService,
              private loadingIndicator: LoadingIndicatorService,
              private organizationService: OrganizationService) {
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.organizationForm.controls;
  }

  get isDisabledSaveBtn(): boolean {
    return this.organizationForm.invalid || this.tableauConfigurationFormIsInvalid;
    //return this.submitted ? isFormInvalid : this.submitted && isFormInvalid;
  };

  ngOnInit(): void {
    this.isNewOrganization = (!this.organization || this.organization.id <= 0);
    this.backupOrganizationName = this.organization.name;
    this.getPossibleParentTenants(this.organization.id, this.isSuperAdminRole);
    this.generateOrganizationForm(this.organization);
  }

  onClose() {
    this.activeModal.dismiss(ModalDialogDismissOption.CLOSE);
  }

  onCancel() {
    this.activeModal.dismiss(ModalDialogDismissOption.CANCEL);
  }

  onSave() {
    this.submitted = true;
    // stop here if form is invalid
    if (this.organizationForm.invalid || this.tableauConfigurationFormIsInvalid) {
      return;
    }
    this.verifyIfDatasetNameIsUsed();
  }

  private verifyIfDatasetNameIsUsed() {
    const newDatasetName: string = this.f.name.value;
    if (this.organization.name !== newDatasetName) {
      this.loadingIndicator.show(this.translate.instant('Verify if dataset name already used'));
      this.organizationService.isDatasetNameUsed(newDatasetName).pipe(takeUntil(this.destroy)).subscribe({
        next: (success) => {
          if (success && success.length > 0 && success[0] === true) {
            this.f.name.setErrors({used: true});
          } else {
            this.updateOrganizationProperties();
            this.activeModal.close(this.organization);
          }
          this.loadingIndicator.hide();
        }, error: (error) => {
          console.error('There was an error checking for organisation duplicate name.', JSON.stringify(error));
          this.loadingIndicator.hide();
        }
      });
    } else {
      this.updateOrganizationProperties();
      this.activeModal.close(this.organization);
    }
  }


  private updateOrganizationProperties() {
    this.organization.name = this.f.name.value;
    this.organization.parentTenant = this.f.parentOrganization.value;
    this.organization.description = this.f.description.value;

    this.organization.agreement = this.f.agreement.value;
    this.organization.userAgreementUrl = this.f.userAgreementUrl.value;
    this.organization.privacyPolicyUrl = this.f.privacyPolicyUrl.value;

    if (this.organization.parentTenantName === null || this.organization.parentTenantName === undefined) {
      this.organization.parentTenantName = '';
    }
  }

  public shouldHideParentDropDown(): boolean {
    return !this.isSuperAdminRole && (this.rootOrganizationId === this.organization.id);
  }

  private getPossibleParentTenants(organizationId: number, isSuperAdminRole: boolean): void {
    this.isLoadingOrganizationParents = true;
    if (organizationId === undefined || organizationId === null) {
      organizationId = -1;
    }

    this.organizationService.listPossibleOrganizationParents(organizationId).pipe(takeUntil(this.destroy)).subscribe({
      next: (success) => {
        this.possibleParents = this.addNoParentOption(success, isSuperAdminRole);
        this.isLoadingOrganizationParents = false;
      }, error: (error) => {
        console.error('OrganizationModal. Error loading the list of possible organization parents', JSON.stringify(error));
        this.possibleParents = this.addNoParentOption(null, isSuperAdminRole);
        this.isLoadingOrganizationParents = false;
      }
    });
  }

  private addNoParentOption(parents: Organization[] | null, isSuperAdminRole: boolean): Organization[] {
    if (parents === null) {
      parents = new Array<Organization>();
    }


    if (isSuperAdminRole) {
      const noParentOption = new Organization();
      noParentOption.id = -1;
      noParentOption.name = ' NONE';
      parents.push(noParentOption);
    }
    parents = parents.sort(function (a, b) {
      const textA = a.name.toUpperCase();
      const textB = b.name.toUpperCase();
      if (textA > textB) {
        return (textA < textB) ? -1 : 1;
      } else {
        return (textA < textB) ? -1 : 0;
      }
    });
    return parents;
  }

  onParentOrganizationChange(organizationParent: Organization | undefined) {
    if (organizationParent == undefined) {
      this.organization.parentTenantName = '';
    } else {
      this.organization.parentTenantName = organizationParent.name;
    }
  }

  private generateOrganizationForm(organization: Organization) {
    this.organizationForm = new UntypedFormGroup({
      name: new UntypedFormControl(organization.name, [Validators.required, Validators.pattern(BIGQUERY_VALID_NAME_REGEX)]),
      externalId: new UntypedFormControl(organization.externalId),
      parentOrganizationName: new UntypedFormControl(organization.parentTenantName),
      parentOrganization: new UntypedFormControl(organization.parentTenant, [Validators.required]),

      description: new UntypedFormControl(organization.description, [Validators.required]),
      agreement: new UntypedFormControl(organization.agreement),
      userAgreementUrl: new UntypedFormControl(organization.userAgreementUrl, [Validators.pattern(URL_REGEX)]),
      privacyPolicyUrl: new UntypedFormControl(organization.privacyPolicyUrl, [Validators.pattern(URL_REGEX)]),
    });

    if (this.isSuperAdminRole) {
      this.organizationForm.get('parentTenant')?.setValidators(null);
    } else {
      this.organizationForm.get('parentTenant')?.setValidators([Validators.required]);
    }

  }

  ngOnDestroy() {
    this.destroy.next(true);
    this.destroy.complete();
  }

  onTableauConfigurationFormIsValid($event: boolean) {
    this.tableauConfigurationFormIsInvalid = !$event;
  }

  onUpdatedTableauConfigurationEvent($event: OrganizationTableauConfiguration) {
    this.organization.tableauConfiguration = $event;
  }

}
